/**
 * MimicContext
 *
 * Tracks an active "mimic user" session — the actor is viewing the dashboard
 * (or canvassing app, in a popup) AS another team member. Read-only: real
 * RLS still uses the actor's auth.uid(), so any actual write attempts run
 * under the actor's permissions, not the target's.
 *
 * Two entry points:
 *
 *   1. Dashboard mode — caller has full access to business_role_permissions
 *      via their Supabase session, so we just stash the target identity and
 *      let `useBusinessRole` re-fetch permissions for that user_id from DB.
 *      Started by `startDashboardMimic({ businessId, target })`.
 *
 *   2. Canvassing mode — caller is the popup at `<subdomain>.noknok.pro`,
 *      a different origin from the dashboard. The popup may not have a
 *      Supabase session at all, so it can't query the role tables itself.
 *      Permissions are pre-fetched server-side by `mimic-verify` and handed
 *      in via `applyVerifiedMimicToken`. The hook honors them as-is.
 *
 * State persists in sessionStorage for the lifetime of the tab so a
 * refresh doesn't drop the mimic.
 */

import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "noknok.mimic.v1";

export interface MimicTarget {
    user_id: string;
    full_name: string | null;
    email: string | null;
    role_name: string;
    is_owner: boolean;
}

export type MimicMode = "dashboard" | "canvassing";

export interface MimicActiveState {
    active: true;
    mode: MimicMode;
    businessId: string;
    target: MimicTarget;
    startedAt: number; // unix seconds
    /** Present in canvassing mode (popup origin can't query DB itself). */
    prefetchedPermissions?: Record<string, boolean>;
    prefetchedCanViewAllRecords?: boolean;
}

export interface MimicInactiveState {
    active: false;
}

export type MimicState = MimicActiveState | MimicInactiveState;

interface VerifiedTokenInput {
    businessId: string;
    mode: MimicMode;
    target: MimicTarget;
    permissions: Record<string, boolean>;
    canViewAllRecords: boolean;
}

interface DashboardMimicInput {
    businessId: string;
    target: MimicTarget;
    /**
     * Optional pre-resolved permission flags. Used by the developer
     * "Login As <user>" flow: a platform developer isn't a member/owner of
     * the target business, so their session can't read its RBAC tables under
     * RLS. The flags are resolved server-side (dev-impersonate-resolve) and
     * fed in here so `useBusinessRole` honors them via its prefetch path
     * instead of querying the DB (which would return nothing for a developer).
     */
    prefetchedPermissions?: Record<string, boolean>;
    prefetchedCanViewAllRecords?: boolean;
}

/**
 * Brief overlay state used to soften the swap between actor and target.
 * Without this, the dashboard's permission map flips instantly and the
 * change feels like a glitch — sidebar items pop in/out, scope toggles
 * relock, etc. The overlay covers the dashboard for ~700ms while state
 * settles so the user gets a clear "switching to <name>" beat.
 */
export interface MimicTransitionState {
    phase: "starting" | "stopping";
    target: MimicTarget;
}

interface MimicContextValue {
    state: MimicState;
    transition: MimicTransitionState | null;
    /** Dashboard-mode entry: actor already has DB access. */
    startDashboardMimic: (input: DashboardMimicInput) => void;
    /** Canvassing-mode entry: called from Storefront after mimic-verify returns. */
    applyVerifiedMimicToken: (input: VerifiedTokenInput) => void;
    /** Clears state, calls mimic-stop for the audit row (best-effort). */
    stopMimic: () => Promise<void>;
}

const MimicContext = createContext<MimicContextValue | undefined>(undefined);

function loadFromStorage(): MimicState {
    if (typeof window === "undefined") return { active: false };
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return { active: false };
        const parsed = JSON.parse(raw) as MimicState;
        if (parsed && parsed.active === true && parsed.target?.user_id) return parsed;
        return { active: false };
    } catch {
        return { active: false };
    }
}

function saveToStorage(state: MimicState) {
    if (typeof window === "undefined") return;
    try {
        if (state.active) {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } else {
            window.sessionStorage.removeItem(STORAGE_KEY);
        }
    } catch {
        // ignore quota / private-mode errors
    }
}

/** Total visible duration of the transition overlay, in ms. The actual
 *  permission-map flip happens ~halfway through so the overlay covers
 *  the brief flash of the dashboard re-rendering with the new flags. */
const TRANSITION_TOTAL_MS = 800;
const TRANSITION_FLIP_MS = 350;

export function MimicProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<MimicState>(() => loadFromStorage());
    const [transition, setTransition] = useState<MimicTransitionState | null>(null);
    const stateRef = useRef(state);

    useEffect(() => {
        stateRef.current = state;
        saveToStorage(state);
    }, [state]);

    const startDashboardMimic = useCallback((input: DashboardMimicInput) => {
        setTransition({ phase: "starting", target: input.target });
        // Flip the permission state mid-overlay so the dashboard re-paints
        // under cover of the curtain.
        window.setTimeout(() => {
            setState({
                active: true,
                mode: "dashboard",
                businessId: input.businessId,
                target: input.target,
                startedAt: Math.floor(Date.now() / 1000),
                ...(input.prefetchedPermissions
                    ? {
                          prefetchedPermissions: input.prefetchedPermissions,
                          prefetchedCanViewAllRecords:
                              input.prefetchedCanViewAllRecords ?? false,
                      }
                    : {}),
            });
        }, TRANSITION_FLIP_MS);
        window.setTimeout(() => {
            setTransition(null);
        }, TRANSITION_TOTAL_MS);
    }, []);

    const applyVerifiedMimicToken = useCallback((input: VerifiedTokenInput) => {
        // The token-handshake path on the storefront popup applies the
        // mimic instantly — the popup itself is already a context-switch
        // signal, so we don't show the overlay here. (If we ever want
        // one, gate this on a flag from the caller.)
        setState({
            active: true,
            mode: input.mode,
            businessId: input.businessId,
            target: input.target,
            startedAt: Math.floor(Date.now() / 1000),
            prefetchedPermissions: input.permissions,
            prefetchedCanViewAllRecords: input.canViewAllRecords,
        });
    }, []);

    const stopMimic = useCallback(async () => {
        const current = stateRef.current;
        if (!current.active) return;

        // Show the overlay first, then flip state and fire the stop call.
        setTransition({ phase: "stopping", target: current.target });
        window.setTimeout(() => {
            setState({ active: false });
        }, TRANSITION_FLIP_MS);
        window.setTimeout(() => {
            setTransition(null);
        }, TRANSITION_TOTAL_MS);

        try {
            await supabase.functions.invoke("mimic-stop", {
                body: {
                    business_id: current.businessId,
                    target_user_id: current.target.user_id,
                    mode: current.mode,
                    started_at: current.startedAt,
                },
            });
        } catch (e) {
            console.warn("mimic-stop failed:", e);
        }
    }, []);

    return (
        <MimicContext.Provider
            value={{ state, transition, startDashboardMimic, applyVerifiedMimicToken, stopMimic }}
        >
            {children}
        </MimicContext.Provider>
    );
}

/**
 * Returns the mimic context. If not inside a `<MimicProvider>` (e.g. a
 * legacy mount, or a test), returns a safe no-op shape so callers can read
 * `state.active === false` without special-casing the boundary.
 */
export function useMimic(): MimicContextValue {
    const ctx = useContext(MimicContext);
    if (!ctx) {
        return {
            state: { active: false },
            transition: null,
            startDashboardMimic: () => {},
            applyVerifiedMimicToken: () => {},
            stopMimic: async () => {},
        };
    }
    return ctx;
}

/** Lightweight read-only check — safe outside of MimicProvider too. */
export function useIsMimicking(): boolean {
    const ctx = useContext(MimicContext);
    return Boolean(ctx?.state.active);
}
